import { useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../../projects/projectContext";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import KnowledgeDataSection from "../../reviews/inbox/KnowledgeDataSection";
import { InboxRows } from "../../reviews/inbox/InboxView";
import { myOpenCount, waitingCount } from "../../reviews/inbox/inboxCounts";
import { refreshReviewInbox, revalidateReviewInbox, useReviewInbox } from "../../reviews/inbox/useReviewInbox";
import SidebarShelf from "./SidebarShelf";

/**
 * The project's inbox at the top of the Threads sidebar (ADR-0031 D4, F108, built
 * provisionally; Johannes, 29 Oct: "The sidebar IS the Inbox"). "Waiting for you" holds what
 * the person can answer: change sets to decide, those sent back to them, and knowledge data
 * to review. Their own open proposals, waiting on others, are a shelf like Settled. Each row
 * opens the Thread step that decides it.
 */
export default function ProjectInbox({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const projects = useProjects().projects;
  const basisUpdateId = projects.status === "loaded" ? projects.value.find((project) => project.projectId === projectId)?.basis?.updateChangeSetId ?? null : null;
  const state = useReviewInbox(projectId);
  // Two Threads tabs side by side show two inboxes, so the heading's id is this one's own.
  const waitingHeadingId = useId();
  // Read again whenever the inbox shows, as the page it replaced did: the badge keeps the
  // snapshot alive, so returning to Threads after a decision would otherwise show the old one.
  useEffect(() => { revalidateReviewInbox(projectId); }, [projectId]);
  if (state === null) return null;
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={() => { refreshReviewInbox(projectId); }} compact />;
  const inbox = state.value;
  const mine = myOpenCount(inbox);
  return (
    <>
      {waitingCount(inbox) === 0 ? null : (
        <section aria-labelledby={waitingHeadingId}>
          <h3 id={waitingHeadingId}className="app-text-muted px-2 text-xs font-semibold">{t("reviews.collaboration.inbox.sections.waitingForMe.title")}</h3>
          <InboxRows projectId={projectId} section="waitingForMe" entries={inbox.waitingForMe} basisUpdateId={basisUpdateId} />
          <InboxRows projectId={projectId} section="sentBackToMe" entries={inbox.sentBackToMe} />
          <KnowledgeDataSection projectId={projectId} entries={inbox.knowledgeData.waitingForMe} />
        </section>
      )}
      {mine === 0 ? null : (
        <SidebarShelf storageKey="dmw.threads.shelf.myOpenProposals" title={t("reviews.collaboration.inbox.sections.myOpenProposals.shelf", { count: mine })}>
          {(open) =>
            open ? (
              <>
                <InboxRows projectId={projectId} section="myOpenProposals" entries={inbox.myOpenProposals} />
                <KnowledgeDataSection projectId={projectId} entries={inbox.knowledgeData.myProposals} />
              </>
            ) : null
          }
        </SidebarShelf>
      )}
    </>
  );
}
